import React from 'react';
import { Rows3, CalendarClock, Network, Zap, Database, Server, Gauge, ListOrdered, Coins, FileText, type LucideIcon } from 'lucide-react';
import { PERM, useAbility, type Perm } from 'shared/access';
import { RackSection } from 'features/rack-view';
import { SubscriptionSection } from 'features/subscription';
import { NetworkingSection } from 'features/networking';
import { StorageSetSection } from 'features/storage-set';
import { DirectorsSection } from 'features/directors';
import { CustomMetricsSection } from 'features/custom-metrics';
import { BomSection } from 'features/bom';
import { PricingSection } from 'features/pricing';
import { QuoteDetailsSection } from 'features/quote-details';
import { useVendorCtx } from 'shared/state/configContexts';

export type SectionId = 'rack' | 'subscription' | 'networking' | 'flash' | 'capacity' | 'directors' | 'metrics' | 'bom' | 'pricing' | 'quote';
export type SectionGroup = 'configure' | 'commercial';

export type SectionDef = {
  id: SectionId;
  group: SectionGroup;
  label: string;
  role: string;
  icon: LucideIcon;
  Component: React.ComponentType<{ id: SectionId }>;
  requires: Perm[];
  /** Feature flags / vendor conditions live here, not in components (guidelines §3). */
  enabled?: (ctx: { vendorId: string }) => boolean;
};

const FlashSection = (props: { id: SectionId }) => <StorageSetSection {...props} variant="flash" />;
const CapacitySection = (props: { id: SectionId }) => <StorageSetSection {...props} variant="capacity" />;

/** Workbench order. Phases 2 and 4 replace `Component` per row; nothing else changes. */
export const SECTIONS: SectionDef[] = [
  { id: 'subscription', group: 'configure', label: 'Subscription', role: 'Delivery, term, service', icon: CalendarClock, Component: SubscriptionSection, requires: [] },
  { id: 'networking', group: 'configure', label: 'Networking', role: 'Interface', icon: Network, Component: NetworkingSection, requires: [] },
  { id: 'flash', group: 'configure', label: 'Flash Set', role: 'All-flash nodes', icon: Zap, Component: FlashSection, requires: [] },
  { id: 'capacity', group: 'configure', label: 'Capacity Set', role: 'Hybrid nodes + JBODs', icon: Database, Component: CapacitySection, requires: [] },
  { id: 'directors', group: 'configure', label: 'Directors', role: 'Director nodes', icon: Server, Component: DirectorsSection, requires: [] },
  // The always-visible rack readout (classic draws it in the System Attributes card).
  // Ungated, like Rack View itself. It follows the three sections that put hardware
  // in it, so it reads as their result (it led the page until 2026-10-04).
  { id: 'rack', group: 'configure', label: 'Rack', role: 'Physical layout', icon: Rows3, Component: RackSection, requires: [] },
  // CONF-212: exec-only; EXEC_CUSTOM_METRICS carries the kill switch and the consultation-mode rule.
  { id: 'metrics', group: 'configure', label: 'Custom Metrics', role: 'Exec metric overrides', icon: Gauge, Component: CustomMetricsSection, requires: [PERM.EXEC_CUSTOM_METRICS] },
  { id: 'bom', group: 'commercial', label: 'BOM', role: 'Bill of materials', icon: ListOrdered, Component: BomSection, requires: [PERM.PRICING_VIEW] },
  { id: 'pricing', group: 'commercial', label: 'Pricing', role: 'Solution price and rates', icon: Coins, Component: PricingSection, requires: [PERM.PRICING_VIEW] },
  { id: 'quote', group: 'commercial', label: 'Quote Details', role: 'Customer, opportunity, dates', icon: FileText, Component: QuoteDetailsSection, requires: [PERM.QUOTE_GENERATE] },
];

export function useVisibleSections(): SectionDef[] {
  const ability = useAbility();
  const { vendor } = useVendorCtx();
  return SECTIONS.filter((s) => s.requires.every((p) => ability.can(p)) && (s.enabled?.({ vendorId: vendor.id }) ?? true));
}
